import moment from 'moment-timezone';
import {List, Map} from 'immutable';

import createSelector from 'web-app/util/custom-create-selector';
import ChildBehaviors from 'web-app/behaviors/child-behavior-ids';
import * as ChildSelectors from 'web-app/react/entities/children/selectors';
import * as InstitutionSelectors from 'web-app/react/entities/institutions/selectors';
import {mealTypeColorFromId} from 'web-app/react/institution/settings/meal-settings/meal-types/helpers';

import * as Constants from './constants';

export const daysFromState = state => state.childProfile.activity.days;
const events = (_, {day}) => day.events;

const days = createSelector(
    daysFromState,
    (_, {mealTypes}) => mealTypes,
    (days, mealTypes) =>
        days.map(day =>
            day.merge({
                events: day.events.map(event => {
                    if (event.type === Constants.ORIGINATOR_TYPES.MEAL_REGISTRATION) {
                        return event.merge({
                            embed: event.embed.set(
                                'color',
                                mealTypeColorFromId(mealTypes, event.embed.get('mealTypeId')),
                            ),
                        });
                    }
                    return event;
                }),
            }),
        ),
);

export const sortedDays = createSelector(days, days => {
    return days
        .filter(day => day.events.size > 0)
        .sort((a, b) => {
            if (moment(a.unFormattedDate).isBefore(moment(b.unFormattedDate))) {
                return 1;
            }
            return -1;
        });
});

export const makeGroupedEvents = () =>
    createSelector(events, events => {
        const temporalList = List(Constants.TEMPORAL_GROUPS).map(group => Map({members: List(group), events: List()}));
        const rangeGroups = List(Constants.RANGE_GROUPS).map(group =>
            Map({parent: group.parent, members: List(group.members)}),
        );

        const groupReduction = events.reduce(
            (reduction, event) => {
                const temporals = reduction.get('temporals');

                // Check if event is in a temporal group
                const temporalIndex = temporals.findIndex(temporal => temporal.get('members').includes(event.type));
                if (temporalIndex > -1) {
                    return reduction.updateIn(['temporals', temporalIndex, 'events'], events => events.push(event));
                }

                // Check if event is a valid parent for a range group
                const isParent = rangeGroups.some(group => group.get('parent') === event.type);
                if (isParent) {
                    return reduction.updateIn(['list'], list => list.push(List([event])));
                }

                // Check if event is valid member of a range group
                const rangeGroup = rangeGroups.find(group => group.get('members')?.includes(event.type) ?? false);
                if (rangeGroup) {
                    // Scan through the reduction list and try to find a valid parent
                    const validParentIndex = reduction
                        .get('list')
                        .findIndex(
                            eventOrGroup =>
                                List.isList(eventOrGroup) &&
                                eventOrGroup.some(
                                    maybeParent =>
                                        maybeParent.type === rangeGroup.get('parent') &&
                                        event.from.isBetween(maybeParent.from, maybeParent.to, '[]'),
                                ),
                        );

                    // If we find a valid parent, add the event to its list
                    if (validParentIndex > -1) {
                        return reduction.updateIn(['list', validParentIndex], group => group.push(event));
                    }
                }

                // Event is not part of any group so just add it to the list
                return reduction.updateIn(['list'], list => list.push(event));
            },
            Map({
                list: List(),
                temporals: temporalList,
            }),
        );

        return groupReduction
            .get('temporals')
            .filter(temp => temp.get('events').size)
            .map(temp => temp.get('events'))
            .concat(groupReduction.get('list'));
    });

const sortGroupOrEvents = events =>
    events.sort((a, b) => {
        const aFrom = List.isList(a) ? a.first().to || a.first().from : a.to || a.from;
        const bFrom = List.isList(b) ? b.first().to || b.first().from : b.to || b.from;

        if (aFrom && bFrom && aFrom.isBefore(bFrom)) {
            return 1;
        }
        if (!bFrom) {
            return 1;
        }
        return -1;
    });

export const makeGroupSortedEvents = () =>
    createSelector(makeGroupedEvents(), events =>
        sortGroupOrEvents(events.map(e => (List.isList(e) ? sortGroupOrEvents(e) : e))),
    );

export const canCheckOn = createSelector(
    ChildSelectors.getChildByProps,
    child => child.isSleeping && child.behaviors.includes(ChildBehaviors.CanCheckSleep),
);

export const napType = createSelector(ChildSelectors.getChildByProps, child =>
    child.isSleeping ? 'END_NAP' : 'START_NAP',
);

export const groupId = createSelector(ChildSelectors.getChildByProps, child => child.groupId);

const canCheckInChildren = createSelector(
    InstitutionSelectors.canCheckChildrenInOrOut,
    canCheckChildrenInOrOut => canCheckChildrenInOrOut,
);

export const canCheckIn = createSelector(
    ChildSelectors.getChildByProps,
    canCheckInChildren,
    (child, canCheckInChildren) => canCheckInChildren && child.behaviors.includes(ChildBehaviors.CanCheckinChild),
);

export const canCheckOut = createSelector(
    ChildSelectors.getChildByProps,
    canCheckInChildren,
    (child, canCheckInChildren) => canCheckInChildren && child.behaviors.includes(ChildBehaviors.CanCheckoutChild),
);

export const childName = createSelector(ChildSelectors.getChildByProps, child => child && child.name.fullName);

export const canManageNaps = createSelector(ChildSelectors.getChildByProps, child =>
    child.behaviors.includes(ChildBehaviors.CanManageNaps),
);

export const canManageDiaperChanges = createSelector(ChildSelectors.getChildByProps, child =>
    child.behaviors.includes(ChildBehaviors.CanManageDiaperChanges),
);

export const canRegisterMeal = createSelector(ChildSelectors.getChildByProps, child =>
    child.behaviors.includes(ChildBehaviors.CanRegisterMeals),
);

export const canAddDiaperToiletAccident = createSelector(
    InstitutionSelectors.canAddDiaperToiletAccident,
    canAddDiaperToiletAccident => canAddDiaperToiletAccident,
);

export const canRegisterCream = createSelector(ChildSelectors.getChildByProps, child =>
    child.behaviors.includes(ChildBehaviors.CanRegisterCream),
);
